import { useState, useEffect } from 'react';
import axios from 'axios';
import { Target, Calendar, CheckCircle, Circle, Sparkles, Lock, AlertTriangle } from 'lucide-react';
import Loader2 from '@/components/brand/Loader2';
import HeavyProgressBar from '@/components/brand/HeavyProgressBar';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import CheckpointDialog from './CheckpointDialog';
import BlockDetailModal from './BlockDetailModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MacroplanCard — shows the user's full macroplan with block progression.
 *
 * Renders:
 *  - Goal + total duration + projected weekly rate
 *  - Block list with the current block highlighted
 *  - Progress bar through the macroplan
 *  - Current block: day N of 28 + countdown to next checkpoint
 */
const MacroplanCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkpointOpen, setCheckpointOpen] = useState(false);
  // Sprint F: bloque abierto para ver detalle
  const [openBlockNumber, setOpenBlockNumber] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/methodology/me/macroplan`);
      setData(res.data);
    } catch (err) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <Card className="card-default">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { macroplan, current_block } = data;
  const totalWeeks = macroplan.total_weeks;
  const totalBlocks = macroplan.total_blocks;
  const currentBlockNumber = macroplan.current_block_number;
  const blocks = macroplan.blocks || [];

  // Day N of 28 for the current block
  const blockStartedAt = current_block?.started_at ? new Date(current_block.started_at) : null;
  const blockWeeks = current_block?.weeks || 4;
  const blockTotalDays = blockWeeks * 7;
  const blockExpectedEnd = current_block?.expected_end_date
    ? new Date(current_block.expected_end_date)
    : null;
  const now = new Date();
  const dayInBlock = blockStartedAt
    ? Math.min(blockTotalDays, Math.max(1, Math.floor((now - blockStartedAt) / 86400000) + 1))
    : 1;
  const daysUntilCheckpoint = blockExpectedEnd
    ? Math.max(0, Math.ceil((blockExpectedEnd - now) / 86400000))
    : null;

  // Overall macroplan progress
  const completedWeeks = (currentBlockNumber - 1) * blockWeeks + (dayInBlock / 7);
  const progressPct = Math.min(100, Math.round((completedWeeks / totalWeeks) * 100));

  // Goal label (human)
  const goalLabels = {
    fat_loss: 'Pérdida de grasa',
    recomposition: 'Recomposición corporal',
    muscle_gain: 'Ganancia muscular',
    performance: 'Rendimiento deportivo',
    maintenance: 'Mantenimiento y salud',
  };
  const goalLabel = goalLabels[macroplan.goal] || macroplan.goal;

  // Target metric label
  let targetLabel = null;
  if (macroplan.target_metric) {
    const tm = macroplan.target_metric;
    if (tm.metric === 'weight_kg' && tm.from && tm.to) {
      const delta = Math.abs(tm.from - tm.to);
      const verb = tm.to < tm.from ? 'Perder' : 'Ganar';
      targetLabel = `${verb} ${delta.toFixed(1)} kg`;
    }
  }

  return (
    <Card className="card-default" data-testid="macroplan-card">
      <CardContent className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Target className="w-4 h-4 text-primary" />
              <span className="text-primary text-xs uppercase tracking-wider font-medium">Tu macroplan</span>
            </div>
            <h2 className="font-teko text-2xl text-white">
              {targetLabel || goalLabel}
            </h2>
            <p className="text-zinc-400 text-sm mt-1">
              {totalWeeks} semanas · {totalBlocks} bloques de {blockWeeks} semanas
              {macroplan.projected_weekly_rate ? ` · ~${macroplan.projected_weekly_rate} kg/sem proyectado` : ''}
            </p>
          </div>
          <div className="text-right">
            <p className="font-display font-bold text-3xl text-white leading-none">[{progressPct} %]</p>
            <p className="text-zinc-500 text-xs uppercase tracking-wider">Avance</p>
          </div>
        </div>

        {/* Progress bar */}
        <div>
          <HeavyProgressBar value={progressPct} size="sm" />
          <div className="hidden">
            <div
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        {/* Block list — Sprint F: clickeable + lock indicator */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-zinc-500 text-xs uppercase tracking-wider">Bloques</p>
            <p className="text-zinc-500 text-xs">Toca un bloque para ver el detalle</p>
          </div>
          <div className="space-y-2">
            {blocks.map((block) => {
              const isPast = block.number < currentBlockNumber;
              const isCurrent = block.number === currentBlockNumber;
              const isUnlocked = block.unlocked !== false;
              return (
                <button
                  key={block.number}
                  type="button"
                  onClick={() => setOpenBlockNumber(block.number)}
                  className={`w-full text-left flex items-start gap-3 p-3 rounded-sm border transition-colors ${
                    isCurrent
                      ? 'border-primary/50 bg-primary/5 hover:bg-primary/10'
                      : isPast
                      ? 'border-zinc-800 bg-zinc-900/50 opacity-60 hover:opacity-100'
                      : 'border-zinc-800 bg-zinc-900/30 hover:bg-zinc-900/60'
                  }`}
                  data-testid={`macroplan-block-${block.number}`}
                >
                  {isPast ? (
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  ) : isCurrent ? (
                    <div className="w-4 h-4 rounded-full bg-primary flex-shrink-0 mt-0.5 animate-pulse" />
                  ) : (
                    <Circle className="w-4 h-4 text-zinc-700 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className={`text-sm font-medium flex items-center gap-1.5 ${isCurrent ? 'text-white' : 'text-zinc-300'}`}>
                        Bloque {block.number}: {phaseLabels[block.phase] || block.phase}
                        {!isUnlocked && (
                          <Lock className="w-3 h-3 text-amber-400" title="Bloqueado" />
                        )}
                      </p>
                      <span className="text-zinc-500 text-xs whitespace-nowrap">
                        {block.weeks} sem
                      </span>
                    </div>
                    <p className="text-zinc-500 text-xs mt-0.5">{block.focus}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Current block detail */}
        {current_block && (
          <div className="border-t border-zinc-800 pt-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-zinc-500 text-xs uppercase tracking-wider">Día actual</p>
                <p className="font-teko text-2xl text-white">
                  Día {dayInBlock} <span className="text-zinc-500 text-lg">de {blockTotalDays}</span>
                </p>
              </div>
              {daysUntilCheckpoint !== null && (
                <div className="text-right">
                  <p className="text-zinc-500 text-xs uppercase tracking-wider">Próximo checkpoint</p>
                  <p className="font-teko text-xl text-primary flex items-center justify-end gap-1">
                    <Calendar className="w-4 h-4" />
                    {daysUntilCheckpoint === 0
                      ? 'Hoy'
                      : daysUntilCheckpoint === 1
                      ? 'Mañana'
                      : `En ${daysUntilCheckpoint} días`}
                  </p>
                </div>
              )}
            </div>

            {/* Checkpoint CTA — shown when checkpoint is due (<=3 days OR overdue) */}
            {daysUntilCheckpoint !== null && daysUntilCheckpoint <= 3 && (
              <div className="mt-4 p-3 bg-primary/10 border border-primary/30 rounded-sm flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
                  <p className="text-zinc-300 text-sm">
                    {daysUntilCheckpoint === 0
                      ? 'Es hora de tu checkpoint'
                      : `Tu checkpoint en ${daysUntilCheckpoint} ${daysUntilCheckpoint === 1 ? 'día' : 'días'}`}
                  </p>
                </div>
                <Button
                  className="btn-primary"
                  size="sm"
                  onClick={() => setCheckpointOpen(true)}
                  data-testid="checkpoint-btn"
                >
                  Hacer checkpoint
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Checkpoint Dialog */}
        <CheckpointDialog
          open={checkpointOpen}
          onOpenChange={setCheckpointOpen}
          currentBlockNumber={currentBlockNumber}
          onSuccess={load}
        />

        {/* Sprint F: detalle de bloque + pago adelantado */}
        <BlockDetailModal
          blockNumber={openBlockNumber}
          onClose={() => setOpenBlockNumber(null)}
          onUnlocked={load}
        />

        {/* Warnings */}
        {macroplan.warnings && macroplan.warnings.length > 0 && (
          <div className="border-t border-zinc-800 pt-4 space-y-1">
            {macroplan.warnings.map((w, i) => (
              <p key={i} className="text-amber-400 text-xs flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />
                <span>{w}</span>
              </p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const phaseLabels = {
  adaptation: 'Adaptación',
  deficit_main: 'Déficit principal',
  acceleration: 'Aceleración',
  stabilization: 'Estabilización',
  baseline: 'Línea base',
  hypertrophy: 'Hipertrofia',
  intensification: 'Intensificación',
  consolidation: 'Consolidación',
  foundation: 'Fundamento',
  strength: 'Fuerza',
  peak: 'Peak',
  accumulation: 'Acumulación',
  realization: 'Realización',
  deload: 'Descarga',
  habit_build: 'Construcción de hábitos',
};

export default MacroplanCard;
